import type { AppViewSheetKind } from "@/v2-backoffice-client/app/AppRouter"
import type { FeatureSurfaceDecideMutationVariables } from "@/v2-backoffice-client/license/changes/__types__/license.changes.decide.mutation"
import type { FeatureContentDecideMutationVariables } from "@/v2-backoffice-client/license/changes/__types__/license.content.decide.mutation"

// The two panels the Feature access board opens (?v=<kind>:<code>), under
// the keys AppRouter registers them by (`satisfies` keeps the two in step).
// A capability is addressed by its surface code and map content by its
// feature code: the code is what the decision is written against and what
// the console deep-links by, and it is stable where a feed row id is not
// (a moved capability has two rows).
export const CAPABILITY_SHEET_KIND =
  "capability" satisfies NonNullable<AppViewSheetKind>
export const MAP_CONTENT_SHEET_KIND =
  "mapContent" satisfies NonNullable<AppViewSheetKind>

/** The capability panel; `isNested` pushes it onto an open panel's trail. */
export function capabilitySheetHref(code: string, isNested = false) {
  return `${isNested ? ">>" : ""}?v=${CAPABILITY_SHEET_KIND}:${code}`
}

/** The map-content panel (a layer or base map, as this board sees it). */
export function mapContentSheetHref(code: string, isNested = false) {
  return `${isNested ? ">>" : ""}?v=${MAP_CONTENT_SHEET_KIND}:${code}`
}

/** The package panel, where the package's contents are composed. */
export function packageSheetHref(id: number, isNested = false) {
  return `${isNested ? ">>" : ""}?v=featureGroup:${id}`
}

/** The customer panel, for a customer named in a lock's impact. */
export function customerSheetHref(externalCustomerId: string) {
  return `>>?v=customer:${externalCustomerId}`
}

// Where a surface's provenance links lead. One definition for the Changes
// feed and the package editor's hover cards.
export const LINEAR_ISSUE_URL = "https://linear.app/placepoint/issue/"
export const GITHUB_PULL_REQUEST_URL =
  "https://github.com/placepoint-no/placepoint/pull/"
export const GITHUB_COMMIT_URL =
  "https://github.com/placepoint-no/placepoint/commit/"

// common.FeatureSurfaceChangeKinds
export const FEATURE_SURFACE_CHANGE_KIND = {
  added: 1,
  removed: 2,
  moved: 3,
} as const

// The GraphQL FeatureSurfaceDecision enum, as the decide mutation takes it.
// String literals typed as the enum's values, so call sites compare and
// send without importing the generated enum object at runtime (the
// userIdentity.constants pattern).
export type FeatureSurfaceDecisionValue =
  FeatureSurfaceDecideMutationVariables["decision"]

export const FEATURE_SURFACE_DECISION = {
  everyone: "EVERYONE",
  packages: "PACKAGES",
  undecided: "UNDECIDED",
} as const satisfies Record<string, `${FeatureSurfaceDecisionValue}`>

// The GraphQL FeatureContentDecision enum, as the content decide mutation
// takes it: the same two words, no UNDECIDED (content arrives free and
// has nothing to confirm).
export type FeatureContentDecisionValue =
  FeatureContentDecideMutationVariables["decision"]

export const FEATURE_CONTENT_DECISION = {
  everyone: "EVERYONE",
  packages: "PACKAGES",
} as const satisfies Record<string, `${FeatureContentDecisionValue}`>
